import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Camera, X, ChevronRight } from 'lucide-react';
import api, { uploadMedia } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import ImageCropper from '../components/ImageCropper';

const INTEREST_OPTIONS = [
  'coffee', 'food', 'fitness', 'wellness', 'yoga', 'sports', 'padel',
  'networking', 'tech', 'art', 'culture', 'photography', 'music',
  'nightlife', 'travel', 'beach', 'brunch', 'fashion', 'social',
  'reading', 'meditation', 'outdoors', 'shopping'
];

const PRIVACY_OPTIONS = {
  followers: [
    { value: 'everyone', label: 'Everyone' },
    { value: 'followers', label: 'Followers only' },
    { value: 'attendees', label: 'Previous attendees only' },
    { value: 'nobody', label: 'Nobody' },
  ],
  following: [
    { value: 'everyone', label: 'Everyone' },
    { value: 'only_me', label: 'Only me' },
  ],
  plans: [
    { value: 'everyone', label: 'Everyone' },
    { value: 'followers', label: 'Followers only' },
    { value: 'attendees', label: 'Previous attendees only' },
    { value: 'hidden', label: 'Hidden' },
  ],
};

const EMIRATES = ['Abu Dhabi', 'Dubai', 'Sharjah', 'Ajman', 'Ras Al Khaimah', 'Fujairah', 'Umm Al Quwain'];

export default function EditProfile() {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [form, setForm] = useState({
    display_name: '', bio: '', city: '', interests: [],
    privacy_followers: 'everyone', privacy_following: 'everyone', privacy_plans: 'everyone'
  });
  const [saving, setSaving] = useState(false);
  const [cropperImage, setCropperImage] = useState(null);
  const [cropperType, setCropperType] = useState(null);
  const [showPrivacy, setShowPrivacy] = useState(false);
  // Q39 — compat catalog loaded once; user-side selections live in `form`
  const [catalog, setCatalog] = useState(null);

  useEffect(() => {
    api.get('/users/me/compat-catalog').then(r => setCatalog(r.data)).catch(() => setCatalog(null));
  }, []);

  useEffect(() => {
    if (user) {
      setForm({
        display_name: user.display_name || '',
        bio: user.bio || '',
        city: user.city || '',
        interests: user.interests || [],
        privacy_followers: user.privacy_followers || 'everyone',
        privacy_following: user.privacy_following || 'everyone',
        privacy_plans: user.privacy_plans || 'everyone',
        // Q39 fields
        looking_for: user.looking_for || [],
        personality: user.personality || [],
        group_style: user.group_style || [],
        active_times: user.active_times || [],
        languages: user.languages || [],
        nationality: user.nationality || '',
        weekend_vibe: user.weekend_vibe || '',
        ask_me_about: user.ask_me_about || [],
        current_obsession: user.current_obsession || '',
        dream_destination: user.dream_destination || '',
      });
    }
  }, [user]);

  const update = (field, val) => setForm(f => ({ ...f, [field]: val }));

  const toggleInterest = (interest) => {
    setForm(f => ({
      ...f,
      interests: f.interests.includes(interest)
        ? f.interests.filter(i => i !== interest)
        : [...f.interests, interest]
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/auth/me', {
        display_name: form.display_name,
        bio: form.bio,
        city: form.city,
        interests: form.interests,
        privacy_followers: form.privacy_followers,
        privacy_following: form.privacy_following,
        privacy_plans: form.privacy_plans,
        // Q39 — compatibility profile
        looking_for: form.looking_for,
        personality: form.personality,
        group_style: form.group_style,
        active_times: form.active_times,
        languages: form.languages,
        nationality: form.nationality,
        weekend_vibe: form.weekend_vibe,
        ask_me_about: form.ask_me_about,
        current_obsession: form.current_obsession,
        dream_destination: form.dream_destination,
      });
      updateUser(data.user);
      navigate('/profile');
    } catch { /* keep user on page */ }
    setSaving(false);
  };

  const toggleChip = (field, value, cap = null) => {
    setForm(f => {
      const cur = f[field] || [];
      if (cur.includes(value)) return { ...f, [field]: cur.filter(x => x !== value) };
      if (cap && cur.length >= cap) return f;
      return { ...f, [field]: [...cur, value] };
    });
  };

  const addTagInput = (field, value, cap = 5) => {
    const v = (value || '').trim();
    if (!v) return;
    setForm(f => {
      const cur = f[field] || [];
      if (cur.includes(v) || cur.length >= cap) return f;
      return { ...f, [field]: [...cur, v] };
    });
  };

  const removeTag = (field, value) => {
    setForm(f => ({ ...f, [field]: (f[field] || []).filter(x => x !== value) }));
  };

  const handlePhotoSelect = (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCropperImage(URL.createObjectURL(file));
    setCropperType(type);
    e.target.value = '';
  };

  const handleCropSave = async (croppedFile) => {
    try {
      const url = (await uploadMedia(croppedFile)).url;
      const { data } = await api.put('/auth/me', { profile_photo: url });
      updateUser(data.user);
    } catch {}
    setCropperImage(null);
    setCropperType(null);
  };

  return (
    <div className="content-area animate-fade-in" data-testid="edit-profile-page">
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-5 pb-3">
        <button onClick={() => navigate(-1)} data-testid="edit-back">
          <ArrowLeft size={22} className="text-[#111111]" />
        </button>
        <h1 className="text-lg font-bold text-[#111111]">Edit Profile</h1>
        <button
          onClick={handleSave}
          disabled={saving}
          data-testid="edit-save-btn"
          className="text-sm font-bold text-[#3F5200] active:opacity-70 disabled:opacity-40"
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>

      <div className="px-4 pb-8 space-y-6">
        {/* Photo */}
        <div className="flex justify-center">
          <div className="relative">
            {user?.profile_photo ? (
              <img loading="lazy" decoding="async" src={user.profile_photo} alt="" className="w-24 h-24 rounded-full object-cover border-2 border-black/[0.06]" />
            ) : (
              <div className="w-24 h-24 rounded-full bg-[#F1F0EA] flex items-center justify-center text-3xl font-bold text-[#9B9EA3]">
                {(user?.display_name || '?')[0]}
              </div>
            )}
            <label className="absolute -bottom-1 -right-1 bg-[#B6E600] rounded-full p-2 cursor-pointer active:scale-90 transition-transform shadow-sm">
              <Camera size={14} className="text-[#111111]" />
              <input type="file" accept="image/*" className="hidden" onChange={(e) => handlePhotoSelect(e, 'profile')} data-testid="edit-photo-upload" />
            </label>
          </div>
        </div>

        {/* Name */}
        <div>
          <label className="block text-xs font-medium text-[#9B9EA3] uppercase tracking-wider mb-2">Name</label>
          <input
            data-testid="edit-name"
            type="text"
            value={form.display_name}
            onChange={e => update('display_name', e.target.value)}
            className="w-full bg-white text-[#111111] rounded-2xl px-4 py-3 text-sm border border-black/[0.06] focus:outline-none focus:border-[#B6E600]/40 placeholder-[#B0B3B8]"
          />
        </div>

        {/* Bio */}
        <div>
          <label className="block text-xs font-medium text-[#9B9EA3] uppercase tracking-wider mb-2">
            Bio <span className="text-[#B0B3B8]">({form.bio.length}/120)</span>
          </label>
          <textarea
            data-testid="edit-bio"
            value={form.bio}
            onChange={e => update('bio', e.target.value.slice(0, 120))}
            placeholder="Add a bio..."
            rows={2}
            maxLength={120}
            className="w-full bg-white text-[#111111] rounded-2xl px-4 py-3 text-sm border border-black/[0.06] focus:outline-none focus:border-[#B6E600]/40 placeholder-[#B0B3B8] resize-none"
          />
        </div>

        {/* City */}
        <div>
          <label className="block text-xs font-medium text-[#9B9EA3] uppercase tracking-wider mb-2">City / Emirate</label>
          <div className="flex flex-wrap gap-2">
            {EMIRATES.map(c => (
              <button
                key={c}
                type="button"
                onClick={() => update('city', c)}
                className={`px-4 py-2 rounded-full text-xs font-medium transition-all btn-press ${
                  form.city === c
                    ? 'bg-[#B6E600] text-[#111111] shadow-sm'
                    : 'bg-white text-[#6B6E73] border border-black/[0.06]'
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        {/* Interests */}
        <div>
          <label className="block text-xs font-medium text-[#9B9EA3] uppercase tracking-wider mb-2">Interests</label>
          <div className="flex flex-wrap gap-2">
            {INTEREST_OPTIONS.map(i => (
              <button
                key={i}
                type="button"
                data-testid={`interest-${i}`}
                onClick={() => toggleInterest(i)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize transition-all btn-press ${
                  form.interests.includes(i)
                    ? 'bg-[#3F5200]/10 text-[#3F5200] border border-[#6B8A00]/30'
                    : 'bg-white text-[#9B9EA3] border border-black/[0.06]'
                }`}
              >
                {i}
              </button>
            ))}
          </div>
        </div>

        {/* Q39 — Compatibility profile (Looking For, Personality, Group Style, Active Times, Languages, Nationality, Weekend vibe, Conversation starters) */}
        {catalog && (
          <CompatSection
            catalog={catalog}
            form={form}
            update={update}
            toggleChip={toggleChip}
            addTag={addTagInput}
            removeTag={removeTag}
          />
        )}

        {/* Privacy Settings */}
        <div>
          <button
            data-testid="toggle-privacy"
            onClick={() => setShowPrivacy(!showPrivacy)}
            className="flex items-center justify-between w-full py-3 border-t border-black/[0.04]"
          >
            <span className="text-sm font-medium text-[#111111]">Privacy Settings</span>
            <ChevronRight size={18} className={`text-[#9B9EA3] transition-transform ${showPrivacy ? 'rotate-90' : ''}`} />
          </button>

          {showPrivacy && (
            <div className="space-y-4 pt-2 animate-fade-in">
              {/* Followers visibility */}
              <div>
                <p className="text-xs font-medium text-[#9B9EA3] uppercase tracking-wider mb-2">Who can see your followers</p>
                <div className="flex flex-wrap gap-2">
                  {PRIVACY_OPTIONS.followers.map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => update('privacy_followers', opt.value)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                        form.privacy_followers === opt.value
                          ? 'bg-[#111111] text-white'
                          : 'bg-white text-[#6B6E73] border border-black/[0.06]'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Following visibility */}
              <div>
                <p className="text-xs font-medium text-[#9B9EA3] uppercase tracking-wider mb-2">Who can see your following</p>
                <div className="flex flex-wrap gap-2">
                  {PRIVACY_OPTIONS.following.map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => update('privacy_following', opt.value)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                        form.privacy_following === opt.value
                          ? 'bg-[#111111] text-white'
                          : 'bg-white text-[#6B6E73] border border-black/[0.06]'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Plans visibility */}
              <div>
                <p className="text-xs font-medium text-[#9B9EA3] uppercase tracking-wider mb-2">Who can see your plans</p>
                <div className="flex flex-wrap gap-2">
                  {PRIVACY_OPTIONS.plans.map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => update('privacy_plans', opt.value)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                        form.privacy_plans === opt.value
                          ? 'bg-[#111111] text-white'
                          : 'bg-white text-[#6B6E73] border border-black/[0.06]'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Image Cropper */}
      {cropperImage && (
        <ImageCropper
          imageSrc={cropperImage}
          onSave={handleCropSave}
          onCancel={() => { setCropperImage(null); setCropperType(null); }}
          aspect={cropperType === 'profile' ? 1 : 16 / 9}
          circular={cropperType === 'profile'}
        />
      )}
    </div>
  );
}


/**
 * Q39 — CompatSection
 * Renders 8 grouped sub-sections so users build a "would I get along with
 * this person?" profile. All chip vocabularies come from the backend
 * /users/me/compat-catalog endpoint so we don't drift between client + server.
 */
function CompatSection({ catalog, form, toggleChip, addTag, removeTag, update }) {
  return (
    <div data-testid="edit-compat-section" className="space-y-5 border-t border-black/[0.04] pt-4">
      <h2 className="text-sm font-extrabold text-[#111] uppercase tracking-wider">Compatibility profile</h2>
      <p className="text-[11px] text-[#7A7E83] -mt-3 leading-relaxed">
        Help people decide if you&rsquo;ll vibe in real life. None of this is required — pick what feels true.
      </p>

      <ChipGroup
        label="I'm looking for"
        field="looking_for"
        options={catalog.looking_for}
        selected={form.looking_for}
        onToggle={toggleChip}
        testIdPrefix="lookfor"
      />

      <ChipGroup
        label={`Personality (up to ${catalog.personality_max})`}
        field="personality"
        options={catalog.personality}
        selected={form.personality}
        onToggle={(field, v) => toggleChip(field, v, catalog.personality_max)}
        testIdPrefix="personality"
      />

      <ChipGroup
        label="Group style"
        field="group_style"
        options={catalog.group_style}
        selected={form.group_style}
        onToggle={toggleChip}
        testIdPrefix="groupstyle"
      />

      <ChipGroup
        label="Usually active"
        field="active_times"
        options={catalog.active_times}
        selected={form.active_times}
        onToggle={toggleChip}
        testIdPrefix="active"
      />

      {/* Languages — free-form add */}
      <TagInputField
        label="Languages"
        placeholder="Type a language and press Enter"
        field="languages"
        tags={form.languages || []}
        onAdd={addTag}
        onRemove={removeTag}
        cap={6}
        testId="languages-input"
      />

      {/* Nationality */}
      <div>
        <label className="block text-xs font-bold text-[#9B9EA3] uppercase tracking-wider mb-2">Nationality</label>
        <input
          data-testid="compat-nationality"
          type="text"
          value={form.nationality || ''}
          onChange={e => update('nationality', e.target.value)}
          placeholder="e.g. Emirati"
          maxLength={48}
          className="w-full bg-white text-[#111] rounded-xl px-3 py-2.5 text-sm border border-black/[0.08] focus:outline-none focus:border-[#6B8A00] placeholder-[#9B9EA3]"
        />
      </div>

      {/* Weekend vibe */}
      <div>
        <label className="block text-xs font-bold text-[#9B9EA3] uppercase tracking-wider mb-2">Weekend vibe</label>
        <input
          data-testid="compat-weekend-vibe"
          type="text"
          value={form.weekend_vibe || ''}
          onChange={e => update('weekend_vibe', e.target.value)}
          placeholder="e.g. Gym → Coffee → Family → Sunset walks"
          maxLength={120}
          className="w-full bg-white text-[#111] rounded-xl px-3 py-2.5 text-sm border border-black/[0.08] focus:outline-none focus:border-[#6B8A00] placeholder-[#9B9EA3]"
        />
      </div>

      <h3 className="text-xs font-extrabold text-[#3F5200] uppercase tracking-wider pt-2">Conversation starters</h3>

      <TagInputField
        label="Ask me about"
        placeholder="e.g. Japan travel, Specialty coffee…"
        field="ask_me_about"
        tags={form.ask_me_about || []}
        onAdd={addTag}
        onRemove={removeTag}
        cap={5}
        testId="ask-me-about-input"
      />

      <div>
        <label className="block text-xs font-bold text-[#9B9EA3] uppercase tracking-wider mb-2">Current obsession</label>
        <input
          data-testid="compat-current-obsession"
          type="text"
          value={form.current_obsession || ''}
          onChange={e => update('current_obsession', e.target.value)}
          placeholder="e.g. Matcha"
          maxLength={80}
          className="w-full bg-white text-[#111] rounded-xl px-3 py-2.5 text-sm border border-black/[0.08] focus:outline-none focus:border-[#6B8A00] placeholder-[#9B9EA3]"
        />
      </div>

      <div>
        <label className="block text-xs font-bold text-[#9B9EA3] uppercase tracking-wider mb-2">Dream destination</label>
        <input
          data-testid="compat-dream-destination"
          type="text"
          value={form.dream_destination || ''}
          onChange={e => update('dream_destination', e.target.value)}
          placeholder="e.g. Patagonia"
          maxLength={80}
          className="w-full bg-white text-[#111] rounded-xl px-3 py-2.5 text-sm border border-black/[0.08] focus:outline-none focus:border-[#6B8A00] placeholder-[#9B9EA3]"
        />
      </div>
    </div>
  );
}

function ChipGroup({ label, field, options, selected, onToggle, testIdPrefix }) {
  return (
    <div>
      <label className="block text-xs font-bold text-[#9B9EA3] uppercase tracking-wider mb-2">{label}</label>
      <div className="flex flex-wrap gap-2">
        {options.map(opt => {
          const on = selected.includes(opt);
          return (
            <button
              key={opt}
              type="button"
              data-testid={`${testIdPrefix}-${opt}`}
              onClick={() => onToggle(field, opt)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold capitalize transition-all ${
                on
                  ? 'bg-[#3F5200] text-white border border-[#3F5200]'
                  : 'bg-white text-[#6B6E73] border border-black/[0.08]'
              }`}
            >
              {opt.replace(/_/g, ' ')}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function TagInputField({ label, placeholder, field, tags, onAdd, onRemove, cap = 5, testId }) {
  const [draft, setDraft] = useState('');
  return (
    <div>
      <label className="block text-xs font-bold text-[#9B9EA3] uppercase tracking-wider mb-2">{label}</label>
      <div className="flex flex-wrap items-center gap-2">
        {tags.map(t => (
          <span
            key={t}
            data-testid={`${field}-tag-${t}`}
            className="inline-flex items-center gap-1 bg-[#3F5200]/10 text-[#3F5200] text-xs font-bold px-2.5 py-1 rounded-full"
          >
            {t}
            <button
              type="button"
              onClick={() => onRemove(field, t)}
              data-testid={`${field}-remove-${t}`}
              className="hover:text-[#FF3B30]"
              aria-label={`Remove ${t}`}
            >
              <X size={12} strokeWidth={3} />
            </button>
          </span>
        ))}
        {tags.length < cap && (
          <input
            data-testid={testId}
            type="text"
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ',') {
                e.preventDefault();
                onAdd(field, draft, cap);
                setDraft('');
              }
            }}
            onBlur={() => { if (draft.trim()) { onAdd(field, draft, cap); setDraft(''); } }}
            placeholder={placeholder}
            className="flex-1 min-w-[140px] bg-white text-[#111] rounded-full px-3 py-1.5 text-xs border border-black/[0.08] focus:outline-none focus:border-[#6B8A00] placeholder-[#9B9EA3]"
          />
        )}
      </div>
    </div>
  );
}
